/**
 * The product's entire status vocabulary and its single mapping onto the Ledger
 * tones. Centralised so no screen can invent a colour.
 *
 * Two palette laws from the design guide are encoded here rather than left to
 * discipline: `danger` (red ink) is reserved for loss, penalty and error, and
 * `warning` (ochre) carries lateness. A status that is merely "not done yet" is
 * `pending`, never red — the whole point of the palette is that red means money
 * lost.
 */
export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "pending";

export interface StatusMeta {
  /** Hebrew label shown to users. */
  label: string;
  tone: Tone;
}

function defineStatuses<T extends Record<string, StatusMeta>>(statuses: T): T {
  return statuses;
}

/** Monthly material-transfer tracking per client per authority. */
export const MonthlyReportingStatus = defineStatuses({
  awaiting_client: { label: "ממתין לאישור", tone: "pending" },
  client_confirmed: { label: "אישר הלקוח", tone: "info" },
  office_confirmed: { label: "אישר המשרד", tone: "success" },
  late: { label: "באיחור", tone: "warning" },
  penalty_risk: { label: "סיכון לקנס", tone: "danger" },
  penalty_incurred: { label: "קנס בפועל", tone: "danger" },
});

/** Authority report file lifecycle, from upload to transmission. */
export const AuthorityReportStatus = defineStatuses({
  uploaded: { label: "הועלה", tone: "neutral" },
  validated: { label: "עבר ולידציה", tone: "info" },
  error: { label: "שגיאה", tone: "danger" },
  transmitted: { label: "שודר", tone: "success" },
});

/** Shared by annual reports and wealth declarations. */
export const WorkspaceStatus = defineStatuses({
  not_started: { label: "לא התחיל", tone: "neutral" },
  in_progress: { label: "בטיפול", tone: "info" },
  requires_completion: { label: "דורש השלמה", tone: "warning" },
  ready: { label: "מוכן לעבודה", tone: "success" },
});

export const TaskStatus = defineStatuses({
  open: { label: "פתוחה", tone: "info" },
  done: { label: "בוצעה", tone: "success" },
  cancelled: { label: "בוטלה", tone: "neutral" },
});

export const InvoiceStatus = defineStatuses({
  paid: { label: "שולם", tone: "success" },
  sent: { label: "נשלח", tone: "info" },
  overdue: { label: "בפיגור", tone: "warning" },
  written_off: { label: "חוב אבוד", tone: "danger" },
});

export const ProfitabilityBand = defineStatuses({
  profitable: { label: "רווחי", tone: "success" },
  marginal: { label: "גבולי", tone: "warning" },
  loss: { label: "הפסדי", tone: "danger" },
  undefined_band: { label: "לא הוגדר", tone: "neutral" },
});

export const EngagementLevel = defineStatuses({
  excellent: { label: "מצוין", tone: "success" },
  active: { label: "פעיל", tone: "info" },
  passive: { label: "פאסיבי", tone: "pending" },
  uncooperative: { label: "לא משתף פעולה", tone: "warning" },
});

export const SignatureStatus = defineStatuses({
  signed: { label: "נחתם", tone: "success" },
  sent: { label: "נשלח לחתימה", tone: "info" },
  awaiting_client: { label: "ממתין ללקוח", tone: "pending" },
  expired: { label: "פג תוקף", tone: "danger" },
});

/** Malware-scan gate. A file is not downloadable until it is `clean`. */
export const ScanStatus = defineStatuses({
  pending: { label: "בסריקה", tone: "pending" },
  clean: { label: "נסרק", tone: "success" },
  quarantined: { label: "בהסגר", tone: "danger" },
  failed: { label: "סריקה נכשלה", tone: "warning" },
});

export type MonthlyReportingStatusKey = keyof typeof MonthlyReportingStatus;
export type AuthorityReportStatusKey = keyof typeof AuthorityReportStatus;
export type WorkspaceStatusKey = keyof typeof WorkspaceStatus;
export type TaskStatusKey = keyof typeof TaskStatus;
export type InvoiceStatusKey = keyof typeof InvoiceStatus;
export type ProfitabilityBandKey = keyof typeof ProfitabilityBand;
export type EngagementLevelKey = keyof typeof EngagementLevel;
export type SignatureStatusKey = keyof typeof SignatureStatus;
export type ScanStatusKey = keyof typeof ScanStatus;
